import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useConversationsTableContext } from "../table/conversations-table-context";

/**
 * Auto-advances to the next conversation when the current one
 * leaves the filtered list (e.g., after a status change).
 */
export function useAutoAdvanceConversation(
  conversationId: ConversationId,
): void {
  const { data, openConversation } = useConversationsTableContext();
  const navigate = useNavigate();
  const location = useLocation();
  const team = useRequiredContext(TeamContext);

  const wasInListRef = useRef(false);
  const nextConversationIdRef = useRef<ConversationId | null>(null);

  useEffect(() => {
    const currentIndex = data.findIndex((c) => c.id === conversationId);
    if (currentIndex !== -1) {
      wasInListRef.current = true;
      if (currentIndex < data.length - 1) {
        nextConversationIdRef.current = data[currentIndex + 1]!.id;
      } else if (currentIndex > 0) {
        nextConversationIdRef.current = data[currentIndex - 1]!.id;
      } else {
        nextConversationIdRef.current = null;
      }
      return;
    }

    if (data.length === 0) {
      wasInListRef.current = false;
      return;
    }
    if (wasInListRef.current) {
      wasInListRef.current = false;
      if (nextConversationIdRef.current) {
        openConversation(nextConversationIdRef.current);
      } else {
        navigate(`/stores/${team._id}/support-v2${location.search}`);
      }
    }
  }, [
    data,
    conversationId,
    openConversation,
    navigate,
    team._id,
    location.search,
  ]);
}
